import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    Chip,
    Collapse,
    Divider,
    Grid,
    IconButton,
    lighten,
    Menu,
    MenuItem,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TransitionGroup } from 'react-transition-group';
import { Job, ProviderType } from '../../../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../../shared/AuthRoles';
import { theme } from '../../../../../shared/theme';
import {
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
    formatCurrencyShort,
} from '../../../../../shared/utils';
import { ServiceType, Status, WeekDays } from '../../../../enums/contractor';
import CustomTooltip from '../../../../shared/CustomTooltip';
import { ModuleContractor } from '../../../../types/contractor';
import { Experience } from '../../../../types/experience';
import { BookingSetMenu, SetMenu } from '../../../../types/set-menu';
import { serviceTypeLeadingIcon } from '../../../Contractors/contractorServiceIcons';
import { getJobsGrandTotal } from '../../EditReservation/reservationEdit.utils';
import {
    reservationEditDescriptionSx,
    reservationEditLabelSx,
    reservationEditValueSx,
} from '../../EditReservation/reservationEditTypography';
import ReservationJobForm, { SetMenuAsProvider } from './ReservationJobForm';
import {
    formatJobScheduleLine,
    formatServiceHours,
    getJobItemsQuantityLabel,
    isJobCancelled,
    jobDurationMinutes,
    OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL,
    OWNER_SET_MENU_CONTRACTOR_ID_CATERING,
} from './reservationJobs.helpers';

export { getJobsGrandTotal } from '../../EditReservation/reservationEdit.utils';

const ReservationJobs = ({
    experienceDetails,
    dateOptions,
    isEditing = false,
    embedInEditDetails = false,
}: {
    experienceDetails: Experience;
    dateOptions: string[];
    isEditing?: boolean;
    embedInEditDetails?: boolean;
}) => {
    const [expandedJobId, setExpandedJobId] = useState<string | number | null>(
        null,
    );
    const [cancelledExpanded, setCancelledExpanded] = useState(false);
    const [jobMenu, setJobMenu] = useState<{
        id: string | number;
        anchorEl: HTMLElement | null;
    }>({ id: '', anchorEl: null });
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const scrollToNewJobIdRef = useRef<string | null>(null);

    const methods = useFormContext();
    const {
        setValue,
        getValues,
        control,
        formState: { errors, submitCount },
        handleSubmit,
        watch,
        trigger,
    } = methods;
    const jobs = useWatch({ control, name: `jobs` as const }) ?? {};

    const allJobsErrors = errors?.jobs as Record<string, unknown> | undefined;

    const jobsWithErrors = Object.entries(allJobsErrors ?? {})
        .map(([id, jobErrors]) => ({
            id,
            errors:
                typeof jobErrors === 'object' && jobErrors !== null
                    ? Object.keys(jobErrors).filter(
                          (error: string) => error !== 'policy',
                      )
                    : [],
        }))
        .filter((job) => job.errors.length > 0);

    useEffect(() => {
        if (submitCount > 0 && jobsWithErrors.length > 0) {
            setExpandedJobId(jobsWithErrors[0].id);
        }
    }, [submitCount]);

    const handleAddJob = () => {
        // Prevent multiple draft rows: force completion of required fields first.
        if (firstIncompleteJobId) {
            scrollToNewJobIdRef.current = firstIncompleteJobId;
            setExpandedJobId(firstIncompleteJobId);
            return;
        }
        const newId = crypto.randomUUID();
        const singleProvider = providers.length === 1 ? providers[0] : null;
        const contractorId = singleProvider?.id ?? '';
        const isSetMenuProvider =
            contractorId === OWNER_SET_MENU_CONTRACTOR_ID_CATERING ||
            contractorId === OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL;
        setValue(
            `jobs.${newId}`,
            {
                id: newId,
                contractor: {
                    id: providers.length === 1 ? providers[0].id : '',
                },
                providerType: isSetMenuProvider
                    ? ProviderType.Owner
                    : ProviderType.Contractor,
                jobDate: '',
                startTime: null,
                endTime: null,
                ownerNotes: '',
                guestNotes: '',
                items: [],
                services: [],
                templateId: '',
                newJob: true,
                contractorHasTemplates: false,
                createdBy: loggedInUser?.role.includes('guest')
                    ? 'guest'
                    : 'owner',
            },
            { shouldDirty: true },
        );
        trigger('requiresServices');
        scrollToNewJobIdRef.current = newId;
        setExpandedJobId(newId);
    };

    const handleRemoveJob = (jobId: string | number) => {
        const jobsRecord = getValues('jobs') ?? {};
        const next = { ...jobsRecord };
        delete next[jobId];
        setValue('jobs', next, { shouldDirty: true });
        setExpandedJobId((prev) => (prev === jobId ? null : prev));
    };

    const firstIncompleteJobId = ((): string | null => {
        for (const [id, job] of Object.entries(jobs as Record<string, any>)) {
            if (!job || isJobCancelled(job) || job.createdBy === 'owner')
                continue;
            const missingRequired =
                !job?.contractor?.id ||
                !job?.jobDate ||
                !job?.startTime ||
                !job?.endTime;
            if (missingRequired) return id;
        }
        return null;
    })();
    const jobsArray = Object.values(jobs ?? {}) as (Job | BookingSetMenu)[];

    const draftBlockTooltip =
        'Please complete your draft request before submitting another one';

    // Cancelled/declined jobs are viewable (expanded) but read-only.

    useLayoutEffect(() => {
        const targetId = scrollToNewJobIdRef.current;
        if (!targetId || expandedJobId !== targetId) {
            return;
        }

        let cancelled = false;
        const timeouts: number[] = [];

        const scrollToJob = (behavior: ScrollBehavior = 'smooth') => {
            if (cancelled || scrollToNewJobIdRef.current !== targetId) {
                return;
            }
            const el = document.getElementById(
                `reservation-job-card-${targetId}`,
            );
            if (!el) {
                return;
            }
            el.scrollIntoView({
                behavior,
                block: 'center',
                inline: 'nearest',
            });
        };

        const finish = () => {
            if (!cancelled && scrollToNewJobIdRef.current === targetId) {
                scrollToNewJobIdRef.current = null;
            }
        };

        // When another job was expanded, its collapse shifts layout above the
        // new card. Scrolling immediately uses stale positions and overshoots.
        const waitForCollapse =
            jobsArray.length > 1 &&
            jobsArray.some((job) => String(job.id) !== targetId);

        if (!waitForCollapse) {
            requestAnimationFrame(() => {
                scrollToJob();
                finish();
            });
        } else {
            timeouts.push(window.setTimeout(() => scrollToJob('auto'), 380));
            timeouts.push(
                window.setTimeout(() => {
                    scrollToJob('smooth');
                    finish();
                }, 720),
            );
        }

        return () => {
            cancelled = true;
            timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [expandedJobId, jobsArray.length]);

    const contractors: ModuleContractor[] = [
        ...(experienceDetails?.contractors ?? []),
    ];

    const setMenuServiceTypes = Array.from(
        new Set(
            experienceDetails.setMenus.map(
                (setMenu: SetMenu) => setMenu.serviceType,
            ),
        ),
    );

    const setMenus: SetMenuAsProvider[] = setMenuServiceTypes.map(
        (serviceType) => ({
            id:
                serviceType === ServiceType.Alcohol
                    ? OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
                    : OWNER_SET_MENU_CONTRACTOR_ID_CATERING,
            name: `${experienceDetails?.owner?.name} (Set Menus)`,
            availableDays: [
                {
                    day: WeekDays.Monday,
                    periods: [],
                },
                {
                    day: WeekDays.Tuesday,
                    periods: [],
                },
                {
                    day: WeekDays.Wednesday,
                    periods: [],
                },
                {
                    day: WeekDays.Thursday,
                    periods: [],
                },
                {
                    day: WeekDays.Friday,
                    periods: [],
                },
                {
                    day: WeekDays.Saturday,
                    periods: [],
                },
                {
                    day: WeekDays.Sunday,
                    periods: [],
                },
            ],
            unavailablePeriods: [],
            services: { type: serviceType, items: [] },
            cutoff: 0,
        }),
    );

    const providers: (ModuleContractor | SetMenuAsProvider)[] = [
        ...contractors,
        ...setMenus,
    ];

    const grandTotal = getJobsGrandTotal(
        Object.values(jobs ?? {}).filter(
            (job: any) => !isJobCancelled(job),
        ) as (Job | BookingSetMenu)[],
    );

    const totalServiceMinutes = useMemo(() => {
        let m = 0;
        for (const job of jobsArray) {
            const j = job as { startTime?: string; endTime?: string };
            if (isJobCancelled(j)) continue;
            const d = jobDurationMinutes(j.startTime ?? '', j.endTime ?? '');
            if (d) m += d;
        }
        return m;
    }, [jobsArray]);

    const activeJobs = jobsArray.filter((job) => !isJobCancelled(job));
    const cancelledJobs = jobsArray.filter((job) => isJobCancelled(job));

    const handleToggleJob = (id: string | number) => {
        setExpandedJobId((prev) => (prev === id ? null : id));
    };

    const closeJobMenu = () => setJobMenu({ id: '', anchorEl: null });

    const getJobTotal = (job: any) => getJobsGrandTotal([job]);

    const serviceLabel = embedInEditDetails
        ? 'Service requests'
        : 'Additional services';

    const getStatusChipSx = (raw: string) => {
        const s = String(raw).toLowerCase();
        switch (s) {
            case 'approved':
            case 'accepted':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.dark.main,
                };
            case 'pending':
            case 'pendingbooking':
            case 'pendingcompletion':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.dark.main,
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.dark.main,
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.dark.main,
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                };
        }
    };

    const metaChipSx = {
        fontSize: 11,
        height: 22,
        fontWeight: 500,
        borderRadius: 1,
        bgcolor: 'background.paper',
        color: 'text.secondary',
        borderColor: 'custom.lightBorderColor',
        '& .MuiChip-label': { px: 0.85 },
    };

    const summarySentence = useMemo(() => {
        const base = `${serviceLabel} total: ${formatCurrencyShort(grandTotal)}`;
        if (totalServiceMinutes > 0) {
            return `${base} for ${formatServiceHours(totalServiceMinutes)} of service`;
        }
        return base;
    }, [grandTotal, serviceLabel, totalServiceMinutes]);

    /**
     * List rows use MUI Collapse without `in` only when wrapped by TransitionGroup (it injects `in`).
     * Cancelled jobs render in a plain Stack — omit outer Collapse or cards stay height 0.
     */
    const renderJobCard = (
        job: Job | BookingSetMenu,
        index: number,
        useTransitionGroupCollapse = true,
    ) => {
        const id = job.id;
        if (!id) return null;
        const contractorId =
            job?.providerType === ProviderType.Owner
                ? job?.serviceType === ServiceType.Catering
                    ? OWNER_SET_MENU_CONTRACTOR_ID_CATERING
                    : OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
                : job?.contractor?.id;
        const contractorMatch = providers.find(
            (c: any) => String(c.id) === String(contractorId),
        );
        const contractorName = contractorMatch?.name ?? '';

        const services: string[] =
            job.providerType === ProviderType.Contractor ? job?.services : [];
        const jobDate = job?.jobDate ? String(job.jobDate) : '';
        const startTime = job?.startTime || '';
        const endTime = job?.endTime || '';
        const cancelled = isJobCancelled(job);
        const accepted =
            String(job?.status ?? '')
                .trim()
                .toLowerCase() === String(Status.Accepted).toLowerCase();
        const readOnlyJob = cancelled || accepted;
        const statusLower = String(job?.status ?? '')
            .trim()
            .toLowerCase();
        const canCancelJob =
            !statusLower ||
            statusLower === String(Status.PendingBooking).toLowerCase() ||
            statusLower === String(Status.Pending).toLowerCase() ||
            statusLower === String(Status.Active).toLowerCase() ||
            accepted;
        const jobTotal = getJobTotal(job);
        const isJobExpanded = expandedJobId === id;
        const mins = jobDurationMinutes(startTime, endTime);
        const durationLabel = mins ? formatServiceHours(mins) : null;
        const scheduleLine = formatJobScheduleLine(jobDate, startTime, endTime);
        const itemsQuantityLabel = getJobItemsQuantityLabel(job);
        const headerTitle = job.serviceType
            ? ` - ${job.serviceType !== ServiceType.Other ? camelCaseToTitleCase(job.serviceType) : 'Service'} by ${contractorName}`
            : '';

        const label = 'Request #' + (index + 1) + headerTitle;
        const ServiceTypeIcon = serviceTypeLeadingIcon(job.serviceType);

        const selectedItems =
            job.providerType === ProviderType.Contractor ? job.items : [];

        const hasSelectedOptions = selectedItems.length > 0;
        const card = (
            <Card
                component='article'
                aria-labelledby={`reservation-job-card-label-${id}`}
                id={`reservation-job-card-${id}`}
                sx={{
                    scrollMarginTop: 72,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isJobExpanded
                        ? alpha(theme.palette.primary.main, 0.42)
                        : theme.palette.custom.lightBorderColor,
                    boxShadow: 0,
                    bgcolor: 'background.paper',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    ...(isJobExpanded && {
                        boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.08)}`,
                    }),
                }}
            >
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            sx={{ minWidth: 0, flex: '1 1 160px' }}
                        >
                            <Box
                                sx={{
                                    width: 18,
                                    height: 18,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    color: (t) =>
                                        alpha(t.palette.primary.main, 0.75),
                                }}
                            >
                                <ServiceTypeIcon sx={{ fontSize: 14 }} />
                            </Box>
                            <Typography
                                component='h4'
                                variant='body3'
                                id={`reservation-job-card-label-${id}`}
                                sx={{
                                    ...reservationEditValueSx,
                                    fontWeight: 700,
                                    letterSpacing: 0.12,
                                    minWidth: 0,
                                }}
                            >
                                {label}
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.75,
                                flex: '0 1 auto',
                            }}
                        >
                            {jobTotal > 0 ? (
                                <Typography
                                    variant='body3'
                                    component='span'
                                    sx={{
                                        ...reservationEditValueSx,
                                        fontWeight: 700,
                                        ...(cancelled && {
                                            textDecoration: 'line-through',
                                            textDecorationThickness: '2px',
                                        }),
                                    }}
                                >
                                    {formatCurrencyShort(jobTotal)}
                                </Typography>
                            ) : null}
                            {job.status ? (
                                <Chip
                                    size='small'
                                    label={camelCaseToTitleCase(
                                        job.status === Status.PendingBooking
                                            ? 'Pending Reservation'
                                            : job.status,
                                    ).toUpperCase()}
                                    sx={{
                                        height: 22,
                                        maxHeight: 22,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        border: 'none',
                                        boxShadow: 'none',
                                        ...getStatusChipSx(job.status),
                                        '& .MuiChip-label': {
                                            px: 1,
                                            py: 0,
                                        },
                                    }}
                                />
                            ) : (
                                <Typography
                                    component='span'
                                    variant='body3'
                                    sx={{
                                        ...reservationEditDescriptionSx,
                                        fontSize: 12,
                                    }}
                                >
                                    Draft
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {!contractorName ? (
                        <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            sx={{ mt: 1 }}
                        >
                            <Box
                                sx={{
                                    width: 18,
                                    height: 18,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    color: (t) =>
                                        alpha(t.palette.primary.main, 0.75),
                                }}
                            >
                                <PersonOutlineOutlined sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography
                                variant='body3'
                                sx={reservationEditDescriptionSx}
                            >
                                Select contractor
                            </Typography>
                        </Stack>
                    ) : !hasSelectedOptions ? (
                        <Stack
                            direction='row'
                            spacing={1}
                            alignItems='flex-start'
                            sx={{ mt: 1 }}
                        >
                            <ListAltOutlined
                                sx={{
                                    fontSize: 18,
                                    color: 'text.secondary',
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant='body3'
                                sx={reservationEditDescriptionSx}
                            >
                                {itemsQuantityLabel}
                            </Typography>
                        </Stack>
                    ) : null}

                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ mt: 0.75 }}
                    >
                        <Box
                            sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: (t) =>
                                    alpha(t.palette.primary.main, 0.75),
                            }}
                        >
                            <AccessTimeOutlined sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography
                            variant='body3'
                            sx={reservationEditDescriptionSx}
                        >
                            {scheduleLine || 'Schedule not set'}
                            {durationLabel ? (
                                <>
                                    {' • '}
                                    <Box
                                        component='span'
                                        sx={{
                                            ...reservationEditValueSx,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {durationLabel}
                                    </Box>
                                </>
                            ) : null}
                        </Typography>
                    </Stack>

                    {hasSelectedOptions ? (
                        <Box sx={{ mt: 1 }}>
                            <Stack
                                direction='row'
                                spacing={1}
                                alignItems='flex-start'
                            >
                                <Box
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        color: (t) =>
                                            alpha(t.palette.primary.main, 0.75),
                                    }}
                                >
                                    <ListAltOutlined sx={{ fontSize: 18 }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={reservationEditDescriptionSx}
                                    >
                                        {`${selectedItems.length} option${
                                            selectedItems.length === 1
                                                ? ''
                                                : 's'
                                        } selected`}
                                    </Typography>

                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {selectedItems.map((item, i) => (
                                            <Stack
                                                key={`${String(item.name)}-${i}`}
                                                direction='row'
                                                spacing={1}
                                                alignItems='center'
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: 'success.main',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant='body3'
                                                    sx={
                                                        reservationEditDescriptionSx
                                                    }
                                                >
                                                    {item.name}
                                                    {` ×${
                                                        Number.isFinite(
                                                            Number(
                                                                item.quantity,
                                                            ),
                                                        ) &&
                                                        Number(item.quantity) >
                                                            0
                                                            ? Number(
                                                                  item.quantity,
                                                              )
                                                            : 1
                                                    }`}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    ) : services.length > 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                mt: 1,
                            }}
                        >
                            {services.map((service) => (
                                <Chip
                                    key={service}
                                    label={camelCaseToSentenceCase(service)}
                                    size='small'
                                    variant='outlined'
                                    sx={metaChipSx}
                                />
                            ))}
                        </Box>
                    ) : null}

                    <Stack
                        direction='row'
                        justifyContent='flex-end'
                        flexWrap='wrap'
                        spacing={1}
                        sx={{ mt: 1.5 }}
                    >
                        {isJobExpanded ? (
                            <Button
                                variant='outlined'
                                color='primary'
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleJob(id);
                                }}
                            >
                                Hide
                            </Button>
                        ) : null}

                        <IconButton
                            size='small'
                            onClick={(e) => {
                                e.stopPropagation();
                                setJobMenu({
                                    id,
                                    anchorEl: e.currentTarget,
                                });
                            }}
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                bgcolor: 'background.paper',
                                width: 38,
                                height: 38,
                            }}
                            aria-label='Open job actions'
                        >
                            <MoreHorizIcon fontSize='small' />
                        </IconButton>

                        <Menu
                            anchorEl={jobMenu.anchorEl}
                            open={jobMenu.id === id && !!jobMenu.anchorEl}
                            onClose={closeJobMenu}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    closeJobMenu();
                                    setExpandedJobId(id);
                                }}
                            >
                                {readOnlyJob ? (
                                    <VisibilityOutlinedIcon
                                        fontSize='small'
                                        style={{ marginRight: 8 }}
                                    />
                                ) : (
                                    <EditOutlinedIcon
                                        fontSize='small'
                                        style={{ marginRight: 8 }}
                                    />
                                )}
                                {readOnlyJob ? 'View details' : 'Edit details'}
                            </MenuItem>
                            {canCancelJob ? (
                                <MenuItem
                                    onClick={() => {
                                        closeJobMenu();
                                        handleRemoveJob(id);
                                    }}
                                    sx={{ color: 'error.main' }}
                                >
                                    <DeleteOutlineIcon
                                        fontSize='small'
                                        style={{ marginRight: 8 }}
                                    />
                                    Cancel request
                                </MenuItem>
                            ) : null}
                        </Menu>
                    </Stack>
                </Box>

                <Collapse in={isJobExpanded} timeout='auto'>
                    <Divider
                        sx={{
                            borderColor: theme.palette.custom.lightBorderColor,
                        }}
                    />
                    <Box
                        sx={{
                            p: { xs: 1.25, sm: 1.5 },
                            bgcolor: embedInEditDetails
                                ? alpha(theme.palette.text.primary, 0.04)
                                : theme.palette.neutral.main,
                        }}
                    >
                        <ReservationJobForm
                            name={`jobs.${id}`}
                            experienceDetails={experienceDetails}
                            dateOptions={dateOptions}
                            numberOfJobs={jobsArray.length}
                            index={index}
                            id={id}
                            isEditing={isEditing}
                            readOnly={readOnlyJob}
                            jobsArray={jobsArray}
                            providers={providers}
                        />
                    </Box>
                </Collapse>
            </Card>
        );

        if (useTransitionGroupCollapse) {
            return (
                <Collapse key={id} timeout={300}>
                    {card}
                </Collapse>
            );
        }

        return (
            <Box key={id} sx={{ width: '100%' }}>
                {card}
            </Box>
        );
    };

    const rootSx = embedInEditDetails
        ? {
              borderRadius: 0,
              border: 'none',
              backgroundColor: 'transparent',
              p: 0,
          }
        : {
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'custom.lightBorderColor',
              backgroundColor: theme.palette.neutral.main,
              p: { xs: 1.25, sm: 1.5 },
          };

    return (
        <Box component='section' aria-label={serviceLabel} sx={rootSx}>
            {jobsArray.length > 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.25,
                        p: { xs: 1.25, sm: 1.5 },
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                        bgcolor: lighten(theme.palette.primary.main, 0.94),
                    }}
                >
                    <Stack
                        direction='row'
                        spacing={1.25}
                        alignItems='center'
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <EventAvailableOutlined
                            sx={{
                                fontSize: 22,
                                color: 'primary.main',
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            variant='body3'
                            sx={{
                                ...reservationEditValueSx,
                                fontWeight: 600,
                            }}
                        >
                            {summarySentence}
                        </Typography>
                    </Stack>
                    <CustomTooltip
                        title={firstIncompleteJobId ? draftBlockTooltip : ''}
                        disableHoverListener={!firstIncompleteJobId}
                        disableFocusListener={!firstIncompleteJobId}
                        disableTouchListener={!firstIncompleteJobId}
                    >
                        <span>
                            <CustomButton
                                fullWidth={isMobile}
                                variant='contained'
                                color='primary'
                                size={isMobile ? 'medium' : 'small'}
                                onClick={handleAddJob}
                                disabled={!!firstIncompleteJobId}
                                sx={{
                                    flexShrink: 0,
                                    fontWeight: 700,
                                    px: 2,
                                    alignSelf: { xs: 'stretch', sm: 'center' },
                                }}
                            >
                                + New request
                            </CustomButton>
                        </span>
                    </CustomTooltip>
                </Box>
            ) : null}

            <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {jobsArray.length === 0 ? (
                    embedInEditDetails ? (
                        <Card
                            variant='outlined'
                            sx={{
                                borderRadius: 2,
                                borderColor: 'custom.lightBorderColor',
                                boxShadow: 'none',
                                overflow: 'hidden',
                                p: { xs: 2, sm: 3 },
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    gap: 1.25,
                                    maxWidth: 560,
                                    mx: 'auto',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 104,
                                        height: 104,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(
                                            theme.palette.primary.main,
                                            0.08,
                                        ),
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                                        mb: 0.75,
                                    }}
                                >
                                    <ListAltOutlined
                                        sx={{
                                            fontSize: 54,
                                            color: 'primary.main',
                                        }}
                                    />
                                </Box>

                                <Typography
                                    variant='body1'
                                    sx={{
                                        ...reservationEditValueSx,
                                        fontWeight: 700,
                                    }}
                                >
                                    No service requests yet
                                </Typography>
                                <Typography
                                    variant='body3'
                                    sx={reservationEditDescriptionSx}
                                >
                                    Add a service request to enhance your
                                    guest&apos;s stay. Select a provider, date,
                                    and details in seconds.
                                </Typography>

                                <CustomTooltip
                                    title={
                                        firstIncompleteJobId
                                            ? draftBlockTooltip
                                            : ''
                                    }
                                    disableHoverListener={!firstIncompleteJobId}
                                    disableFocusListener={!firstIncompleteJobId}
                                    disableTouchListener={!firstIncompleteJobId}
                                >
                                    <span>
                                        <CustomButton
                                            fullWidth={isMobile}
                                            variant='contained'
                                            color='primary'
                                            size={isMobile ? 'medium' : 'large'}
                                            onClick={handleAddJob}
                                            disabled={!!firstIncompleteJobId}
                                            sx={{
                                                mt: 1,
                                                fontWeight: 700,
                                                px: 3,
                                                minWidth: { sm: 320 },
                                            }}
                                        >
                                            + Add service request
                                        </CustomButton>
                                    </span>
                                </CustomTooltip>
                            </Box>
                        </Card>
                    ) : (
                        <Grid
                            sx={{
                                bgcolor: embedInEditDetails
                                    ? 'background.paper'
                                    : 'custom.cardActionBackground',
                                borderRadius: 2,
                                border: `1px dashed ${theme.palette.custom.lightBorderColor}`,
                                p: 1.75,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1.5,
                            }}
                        >
                            <Typography
                                variant='body3'
                                sx={{
                                    ...reservationEditDescriptionSx,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                No requests yet. Use{' '}
                                <Box component='span' sx={{ fontWeight: 700 }}>
                                    + New request
                                </Box>{' '}
                                to add a service request for this reservation.
                            </Typography>
                            <CustomTooltip
                                title={
                                    firstIncompleteJobId
                                        ? draftBlockTooltip
                                        : ''
                                }
                                disableHoverListener={!firstIncompleteJobId}
                                disableFocusListener={!firstIncompleteJobId}
                                disableTouchListener={!firstIncompleteJobId}
                            >
                                <span>
                                    <CustomButton
                                        variant='contained'
                                        color='primary'
                                        size='small'
                                        onClick={handleAddJob}
                                        disabled={!!firstIncompleteJobId}
                                        sx={{
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        + New request
                                    </CustomButton>
                                </span>
                            </CustomTooltip>
                        </Grid>
                    )
                ) : null}

                {activeJobs.length > 0 ? (
                    <Box component='section'>
                        <Typography
                            variant='body3'
                            sx={{
                                ...reservationEditLabelSx,
                                display: 'block',
                                mb: 1,
                            }}
                        >
                            Active / Pending
                        </Typography>
                        <TransitionGroup
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                            }}
                        >
                            {activeJobs.map((job, localIndex) =>
                                renderJobCard(job, localIndex),
                            )}
                        </TransitionGroup>
                    </Box>
                ) : null}

                {cancelledJobs.length > 0 ? (
                    <Accordion
                        expanded={cancelledExpanded}
                        onChange={(_, exp) => setCancelledExpanded(exp)}
                        disableGutters
                        elevation={0}
                        sx={{
                            borderRadius: '8px !important',
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            bgcolor: 'background.paper',
                            overflow: 'hidden',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <ExpandMore sx={{ color: 'text.secondary' }} />
                            }
                            sx={{
                                px: 1.5,
                                py: 1,
                                minHeight: 48,
                                '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    gap: 1,
                                    my: 0.5,
                                },
                            }}
                        >
                            <Typography
                                variant='body3'
                                sx={{
                                    ...reservationEditLabelSx,
                                }}
                            >
                                Cancelled
                            </Typography>
                            <Chip
                                size='small'
                                label={String(cancelledJobs.length)}
                                sx={{
                                    height: 22,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    border: 'none',
                                    boxShadow: 'none',
                                    ...getStatusChipSx('cancelled'),
                                }}
                            />
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                            <Stack spacing={1.25}>
                                {cancelledJobs.map((job, localIndex) =>
                                    renderJobCard(job, localIndex, false),
                                )}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ) : null}
            </Grid>
        </Box>
    );
};

export default ReservationJobs;
